import { useQuery } from "@tanstack/react-query";
import type { DistributionItem } from "@/lib/utils";
import { queryClient } from "@/lib/queryClient";
import { fetchUserProfile } from "@/hooks/useUser";
import {
  reportsService,
  type ReportsDistributionsResponse,
} from "@/services/ReportsService";
import type { ProfileTeamScope } from "@/types/users";

export const REPORTS_STALE_TIME = 1000 * 60 * 5;

export type ReportsLoaderData = {
  teamScope: string;
};

export const getReportsTeamScope = (
  profile: ProfileTeamScope | null | undefined,
): string =>
  profile?.role === "admin" ? "all" : (profile?.team ?? "none");

export const getReportsDistributionsQueryKey = (teamScope: string) =>
  ["reports", "distributions", teamScope] as const;

function toDistributionItems(
  counts: Record<string, number> | undefined,
): DistributionItem[] {
  const sorted = Object.entries(counts ?? {})
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);

  const total = sorted.reduce((sum, item) => sum + item.count, 0);

  return sorted.map(({ label, count }) => ({
    label,
    count,
    percentage:
      total > 0 ? Number(((count / total) * 100).toFixed(1)) : 0,
  }));
}

export const mapReportsDistributions = (
  raw: ReportsDistributionsResponse,
): Record<string, DistributionItem[]> => ({
  "site-types": toDistributionItems(raw.siteTypes),
  geo: toDistributionItems(raw.geo),
  keywords: toDistributionItems(raw.keywords),
  languages: toDistributionItems(raw.languages),
});

export const EMPTY_REPORTS_DISTRIBUTIONS: Record<string, DistributionItem[]> =
  {
    "site-types": [],
    geo: [],
    keywords: [],
    languages: [],
  };

export async function fetchReportsDistributions(): Promise<ReportsDistributionsResponse> {
  const { profile } = await queryClient.fetchQuery({
    queryKey: ["authUser"],
    queryFn: fetchUserProfile,
    staleTime: REPORTS_STALE_TIME,
  });

  return reportsService.fetchAllReportStats(profile);
}

export const useReportsData = (teamScope: string) => {
  return useQuery({
    queryKey: getReportsDistributionsQueryKey(teamScope),
    queryFn: fetchReportsDistributions,
    select: mapReportsDistributions,
    staleTime: REPORTS_STALE_TIME,
  });
};
